import { React, useState, useEffect, useMemo } from "react";
import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import Add from "@mui/icons-material/Add";
import Button from "@mui/joy/Button";
import ConfirmDialog from "./ConfirmDialog";
import Notification from "./Notification";
import AddTripType from "../forms/AddTripType";
import AddPopup from "../popups/AddPopup";
import RDataGrid from "./reusables/RDataGrid";
import { GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { LightTooltip } from "../tooltips/ToolTip";
import PageLoader from "./reusables/PageLoader";

const TripTypes = () => {
  const [isFetchingTripTypes, setIsFetchingTripTypes] = useState(true);
  const [tripTypes, setTripTypes] = useState([]);
  const TRIP_TYPES_URL = "api/v1/triptypes";
  const DELETE_URL = "api/v1/triptypes/";
  const axiosPrivate = useAxiosPrivate();
  const [openPopup, setOpenPopup] = useState(false);
  const [refreshTripTypes, setSetRefreshTripTypes] = useState(null);

  // Edit trip-type
  const [editTripType, setEditTripType] = useState(false);
  const [selectedTripType, setSelectedTripType] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const divStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const onDelete = async (id) => {
    const controller = new AbortController();
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    try {
      const response = await axiosPrivate.delete(DELETE_URL + id, {
        signal: controller.signal,
      });
      var message = response.data.message;
      setNotify({
        isOpen: true,
        message: message,
        type: "success",
      });
      setSetRefreshTripTypes(new Date());
    } catch (error) {
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      setConfirmDialog({
        isOpen: true,
        title: "Failed To Delete Trip-Type",
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: () => {
          onDelete(id);
        },
      });
    }
  };

  useEffect(() => {
    if (!isFetchingTripTypes) {
      setIsFetchingTripTypes(true);
    }

    let isMounted = true;
    const controller = new AbortController();

    const getTripTypes = async () => {
      try {
        const response = await axiosPrivate.get(TRIP_TYPES_URL, {
          signal: controller.signal,
        });
        isMounted && setTripTypes(response.data.data);
        setIsFetchingTripTypes(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setIsFetchingTripTypes(false);
      }
    };

    getTripTypes();

    return () => {
      isMounted = false;
      controller.abort();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [refreshTripTypes]);

  const columns = useMemo(
    () => [
      { field: "id", headerName: "Id", width: 60 },
      { field: "name", headerName: "Name", width: 130 },
      {
        field: "isPickUp",
        headerName: "Trip-Nature",
        width: 300,
        type: "boolean",
        editable: false,
        renderCell: function (params) {
          const isPickUp = params.row.isPickUp;
          return isPickUp ? "Pick-up" : "Drop-off";
        },
      },
      {
        field: "dayTime",
        headerName: "Day-Time",
        width: 210,
        renderCell: function (params) {
          const dayTime = params.row.dayTime;
          return dayTime.name;
        },
      },
      {
        field: "time",
        headerName: "Start-Time",
        width: 200,
        renderCell: function (params) {
          const dayTime = params.row.dayTime;
          return dayTime.time;
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        align: "center",
        headerAlign: "center",
        width: 100,
        sortable: false,
        disableClickEventBubbling: true,
        cellClassName: "actions",
        getActions: (params) => {
          return [
            <LightTooltip title="Edit trip-type">
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                className="textPrimary"
                onClick={function () {
                  const currentRow = params.row;
                  const selectedTripType = JSON.stringify(currentRow, null, 4);
                  setEditTripType(true);
                  setSelectedTripType(selectedTripType);
                  return setOpenPopup(true);
                }}
                color="success"
              />
            </LightTooltip>,
            <LightTooltip title="Delete trip-type">
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                color="error"
                onClick={function () {
                  setConfirmDialog({
                    isOpen: true,
                    title: "Are you sure you want to delete this trip-type?",
                    subTitle: "You can't undo this operation",
                    action: "Delete",
                    onConfirm: () => {
                      onDelete(params.row.id);
                    },
                  });
                }}
              />
            </LightTooltip>,
          ];
        },
      },
    ],
    []
  );

  // Close dialog upon successful day-time add/update
  const closeDialog = (message) => {
    setOpenPopup(false);
    setNotify({
      isOpen: true,
      message: message,
      type: "success",
    });
    setSetRefreshTripTypes(new Date());
  };

  return isFetchingTripTypes ? (
    <PageLoader title="Loading trip-types..." />
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      <Box height={40} />

      {tripTypes?.length ? (
        <Box
          sx={{
            height: 560,
            width: "100%",
          }}
        >
          <div style={divStyle}>
            <Typography
              variant="h4"
              component="h4"
              sx={{ textAlign: "left", mt: 3, mb: 3 }}
            >
              Manage Trip-Types
            </Typography>
            <Button
              size="lg"
              variant="solid"
              color="primary"
              onClick={function () {
                setEditTripType(false);
                setSelectedTripType(null);
                setOpenPopup(true);
              }}
              startDecorator={<Add />}
            >
              Add Trip-Type
            </Button>
          </div>
          <Box sx={{ width: 1218, margin: "0 auto 16px", height: 560 }}>
            <RDataGrid columns={columns} rows={tripTypes} rowHeight={63} />
          </Box>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={editTripType ? "Edit Trip-Type" : "Add Trip-Type"}
          >
            <AddTripType
              closeDialog={closeDialog}
              editTripType={editTripType}
              selectedTripType={selectedTripType}
            />
          </AddPopup>
        </Box>
      ) : (
        <div style={divStyle}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ textAlign: "center", mt: 3, mb: 3 }}
          >
            No trip-types to display
          </Typography>
          <Button
            size="lg"
            variant="solid"
            color="primary"
            onClick={function () {
              setEditTripType(false);
              setSelectedTripType(null);
              setOpenPopup(true);
            }}
            startDecorator={<Add />}
          >
            Add Trip-Type
          </Button>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={editTripType ? "Edit Trip-Type" : "Add Trip-Type"}
          >
            <AddTripType
              closeDialog={closeDialog}
              editTripType={editTripType}
              selectedTripType={selectedTripType}
            />
          </AddPopup>
        </div>
      )}
    </Box>
  );
};

export default TripTypes;
